import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl, getErrorMessage } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

type TemplateConfiguration = {
    logo: File | null;
};

type GenerateUploadUrlResponse = {
    status: number;
    message: string;
    data: {
        uploadUrl: string;
        logoS3Key: string;
        expiresIn: number;
        metadata: {
            originalFileName: string;
            mimeType: string;
            fileSize: number;
            uploadedBy: string;
            uploadTimestamp: string;
        };
    };
};

export const useSaveTemplateConfiguration = (): UseMutationResult<
    void,
    Error,
    TemplateConfiguration
> => {
    const authenticatedFetch = useAuthenticatedFetch(); // Rename for clarity
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ logo }) => {
            let logoS3Key = "";
            let logoFileName = "";
            let logoMimeType = "";

            // Step 1: Handle logo upload if provided
            if (logo) {
                // Generate presigned URL using authenticated fetch
                const uploadUrlPayload = {
                    fileName: logo.name,
                    mimeType: logo.type,
                    fileSize: logo.size,
                };

                const uploadUrlResponse = await authenticatedFetch(
                    apiUrl("/admin/document-template/generate-upload-url"),
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/vnd.api+json",
                        },
                        body: JSON.stringify(uploadUrlPayload),
                    },
                );

                await handleJsonApiError(uploadUrlResponse);
                const uploadUrlData: GenerateUploadUrlResponse = await uploadUrlResponse.json();

                try {
                    // Use regular fetch() instead of authenticated fetch for pre-signed URLs
                    const s3Response = await fetch(uploadUrlData.data.uploadUrl, {
                        method: "PUT",
                        body: logo,
                        headers: {
                            "Content-Type": logo.type, // Match the file's content type
                        },
                    });

                    if (!s3Response.ok) {
                        const errorText = await s3Response.text();
                        console.error("S3 upload error response:", errorText);
                        throw new Error(
                            `S3 upload failed: ${s3Response.status} ${s3Response.statusText} - ${errorText}`,
                        );
                    }
                } catch (error: unknown) {
                    console.info("error", getErrorMessage(error));
                    throw new Error(`S3 upload failed: ${getErrorMessage(error)}`);
                }

                // Store S3 info for confirmation step
                logoS3Key = uploadUrlData.data.logoS3Key;
                logoFileName = logo.name;
                logoMimeType = logo.type;
            } else {
                // Step 1.5: Get existing logo info if no new logo provided
                const currentConfigResponse = await authenticatedFetch(
                    apiUrl("/admin/document-template"),
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/vnd.api+json",
                        },
                    },
                );

                if (currentConfigResponse.ok) {
                    const currentConfig = await currentConfigResponse.json();
                    const configData = currentConfig.data;

                    if (configData.logoS3Key) {
                        logoS3Key = configData.logoS3Key;
                        logoFileName = configData.logoFileName || "";
                        logoMimeType = configData.logoMimeType || "";
                    }
                } else {
                    console.warn(
                        "Could not fetch existing configuration, proceeding without logo info",
                    );
                }
            }

            // Step 2: Confirm upload and save configuration using authenticated fetch
            const confirmPayload = {
                logoS3Key,
                logoFileName,
                logoMimeType,
            };

            const confirmResponse = await authenticatedFetch(
                apiUrl("/admin/document-template/confirm-upload"),
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/vnd.api+json",
                    },
                    body: JSON.stringify(confirmPayload),
                },
            );

            await handleJsonApiError(confirmResponse);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["template-configuration"],
            });
        },
    });
};
